import axios from '../../../config/axios';
import {
  CreateFinancialEntryPayload,
  CreateSplitResponse,
  FinancialEntryDetail,
  FinancialEntryListFilters,
  FinancialEntryListResponse,
  UpdateFinancialEntryPayload,
} from './types';
import { filtersToCustomRules } from './financialFilterConfig';

const API_BASE = `${process.env.REACT_APP_GATEWAY_BASE_URL}/financial/entry`;

const buildListParams = (filters: FinancialEntryListFilters) => {
  const params: Record<string, string | number> = {
    viewBy: filters.viewBy ?? 'entry',
  };

  if (filters.page) params.page = filters.page;
  if (filters.limit) params.limit = filters.limit;
  if (filters.search) params.search = filters.search;
  if (filters.createdAtFrom) params.createdAtFrom = filters.createdAtFrom;
  if (filters.createdAtTo) params.createdAtTo = filters.createdAtTo;
  if (filters.sortBy) params.sortBy = filters.sortBy;

  const customRules = filtersToCustomRules(filters.appliedFilters ?? []);
  if (customRules.length) {
    params.customFilters = JSON.stringify(customRules);
  }

  return params;
};

export interface PaymentMethodOption {
  id: number;
  name: string;
}

export const financialEntryApi = {
  getPaymentMethods: async (): Promise<PaymentMethodOption[]> => {
    const { data } = await axios.get<PaymentMethodOption[]>(`${API_BASE}/payment-methods`);
    return data;
  },

  list: async (filters: FinancialEntryListFilters = {}): Promise<FinancialEntryListResponse> => {
    const { data } = await axios.get<FinancialEntryListResponse>(API_BASE, {
      params: buildListParams(filters),
    });
    return data;
  },

  getById: async (id: number): Promise<FinancialEntryDetail> => {
    const { data } = await axios.get<FinancialEntryDetail>(`${API_BASE}/${id}`);
    return data;
  },

  createSplit: async (payload: CreateFinancialEntryPayload): Promise<CreateSplitResponse> => {
    const { data } = await axios.post<CreateSplitResponse>(`${API_BASE}/split`, payload);
    return data;
  },

  update: async (id: number, payload: UpdateFinancialEntryPayload): Promise<FinancialEntryDetail> => {
    const { data } = await axios.patch<FinancialEntryDetail>(`${API_BASE}/${id}`, payload);
    return data;
  },

  remove: async (id: number): Promise<void> => {
    await axios.delete(`${API_BASE}/${id}`);
  },

  uploadFiles: async (itemId: number, files: File[]) => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    const { data } = await axios.post(`${API_BASE}/item/${itemId}/upload-files`, formData);
    return data;
  },

  getFileUrl: async (
    itemId: number,
    fileId: number,
    forceDownload = false,
  ): Promise<string> => {
    const { data } = await axios.get<{ url: string }>(
      `${API_BASE}/item/${itemId}/files/${fileId}/download`,
      { params: { forceDownload: forceDownload ? 'true' : 'false' } },
    );
    return data.url;
  },

  viewFile: async (itemId: number, fileId: number): Promise<string> =>
    financialEntryApi.getFileUrl(itemId, fileId, false),

  downloadFile: async (itemId: number, fileId: number): Promise<string> =>
    financialEntryApi.getFileUrl(itemId, fileId, true),

  deleteFile: async (itemId: number, fileId: number): Promise<void> => {
    await axios.delete(`${API_BASE}/item/${itemId}/files/${fileId}`);
  },
};

export default financialEntryApi;
